import React, { useEffect, useState } from "react";
import { Alert, Form, Input, Modal, message } from "antd";

import { supabase } from "~/store/initializeStore";

interface AddCustomerFormModel {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export type CreatedCustomer = {
  id: string;
  email: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (customer: CreatedCustomer) => void;
}

export const AddCustomerModal = ({ open, onClose, onCreated }: Props): React.ReactElement => {
  const [form] = Form.useForm<AddCustomerFormModel>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const createCustomer = async (values: AddCustomerFormModel) => {
    const email = values.email.trim().toLowerCase();

    try {
      setSaving(true);

      const { data: existing, error: existingError } = await supabase
        .from("customer")
        .select("id")
        .ilike("email", email)
        .limit(1);

      if (existingError) throw new Error(existingError.message);

      if (existing?.length) {
        void message.warning("Klient z tym adresem e-mail już istnieje");
        return;
      }

      const { data, error } = await supabase
        .from("customer")
        .insert({
          id: crypto.randomUUID(),
          email,
          firstName: values.firstName?.trim() || null,
          lastName: values.lastName?.trim() || null,
          phoneNumber: values.phoneNumber?.trim() || null,
          client: {
            firstName: values.firstName?.trim() || "",
            lastName: values.lastName?.trim() || "",
          },
          source: "manual",
        } as never)
        .select("id, email")
        .single();

      if (error) throw new Error(error.message);

      void message.success("Klient dodany");
      onClose();
      onCreated(data as { id: string; email: string });
    } catch (error) {
      void message.error(`Nie udało się dodać klienta: ${(error as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Dodaj klienta"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Dodaj"
      confirmLoading={saving}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Zamówienia i pakiety klienta łączą się po adresie e-mail"
        description="Jeśli klient ma już zamówienia wystawione na ten adres, pojawią się automatycznie w jego szczegółach."
      />
      <Form form={form} layout="vertical" onFinish={(values) => void createCustomer(values)}>
        <Form.Item
          name="email"
          label="Adres e-mail"
          rules={[
            { required: true, message: "Podaj adres e-mail" },
            { type: "email", message: "Niepoprawny adres e-mail" },
          ]}
        >
          <Input placeholder="klient@example.com" />
        </Form.Item>
        <Form.Item name="firstName" label="Imię">
          <Input />
        </Form.Item>
        <Form.Item name="lastName" label="Nazwisko">
          <Input />
        </Form.Item>
        <Form.Item name="phoneNumber" label="Telefon">
          <Input placeholder="+48..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};
